import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Loader } from '@progress/kendo-react-indicators';
import { useRef, useState } from 'react';
import { useMutation } from 'urql';
import { graphql } from '../gql/index.ts';

interface Props {
  onClose: () => void;
}

const MAX_SUMMARY_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 5000;

const CreateUserFeedbackMutation = graphql(/* GraphQL */ `
  mutation createUserFeedback($feedback: CreateUserFeedbackInput!) {
    createUserFeedback(feedback: $feedback) {
      id
      idReadable
    }
  }
`);

export const MakeAWishModal: React.FC<Props> = ({ onClose }) => {
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [createdIssue, setCreatedIssue] = useState<string | undefined>();
  const [{ fetching: submitting }, createUserFeedback] = useMutation(
    CreateUserFeedbackMutation,
  );
  const inFlight = useRef(false);

  const trimmedSummary = summary.trim();
  const trimmedDescription = description.trim();
  const canSubmit =
    !submitting &&
    trimmedSummary.length > 0 &&
    trimmedSummary.length <= MAX_SUMMARY_LENGTH &&
    trimmedDescription.length > 0 &&
    trimmedDescription.length <= MAX_DESCRIPTION_LENGTH;

  const onSubmit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError(undefined);
    try {
      const result = await createUserFeedback({
        feedback: { summary: trimmedSummary, description: trimmedDescription },
      });
      if (result.error) {
        setError(
          result.error.graphQLErrors[0]?.message ??
            result.error.message ??
            'Unbekannter Fehler beim Senden des Wunsches',
        );
        return;
      }
      const idReadable = result.data?.createUserFeedback?.idReadable;
      if (!idReadable) {
        setError('Unbekannter Fehler beim Senden des Wunsches');
        return;
      }
      setCreatedIssue(idReadable);
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <Dialog
      appendTo={document.getElementById('root')}
      autoFocus={true}
      width="min(600px, 100vw)"
      title="Make a Wish"
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        {createdIssue ? (
          <div className="flex flex-col gap-4">
            <p>
              Vielen Dank! Dein Wunsch wurde unter{' '}
              <span className="font-mono font-semibold">{createdIssue}</span>{' '}
              eingereicht.
            </p>
            <div>
              <Button themeColor="primary" onClick={onClose}>
                Schließen
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-zinc-700">
              Was würdest du dir für die BTS Webwelt wünschen? Beschreibe kurz
              dein Anliegen — wir prüfen jeden Wunsch und melden uns bei
              Rückfragen.
            </p>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">Titel</span>
              <Input
                value={summary}
                onChange={(e) => setSummary(String(e.value ?? ''))}
                maxLength={MAX_SUMMARY_LENGTH}
                placeholder="Kurzbeschreibung des Wunsches"
                autoFocus
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">Details</span>
              <TextArea
                value={description}
                onChange={(e) => setDescription(String(e.value ?? ''))}
                rows={6}
                maxLength={MAX_DESCRIPTION_LENGTH}
                placeholder="Ausführlichere Beschreibung, Anwendungsfall, etc."
              />
            </label>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <div className="flex flex-row gap-2">
              <Button
                themeColor="primary"
                disabled={!canSubmit}
                onClick={(e) => {
                  e.preventDefault();
                  void onSubmit();
                }}
                className="[&>span.k-button-text]:flex [&>span.k-button-text]:items-center"
              >
                {submitting && (
                  <Loader type="pulsing" className="!text-white" />
                )}
                Wunsch absenden
              </Button>
              <Button
                themeColor="secondary"
                disabled={submitting}
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                }}
              >
                Abbrechen
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
